<script lang="ts" setup>
import { SwitchRoot, SwitchThumb } from 'reka-ui';
import { computed, onMounted, ref, useAttrs, useSlots, watch } from 'vue';

import { useDataTestid } from '@/kasten-components/composables';
import type { VueClass } from '@/kasten-components/types';
import { dom } from '@/kasten-components/utils';
import { cnMerge } from '@/utils/utils.js';

import { switchVariants } from './switch-variants';

defineOptions({
  inheritAttrs: false,
});

export interface KaSwitchProps {
  modelValue?: boolean;
  name: string;
  label?: string;
  labelClass?: string;
  hideLabel?: boolean;
  description?: string;
  descriptionClass?: string;
  hideDescription?: boolean;
  disabled?: boolean;
  small?: boolean;
  class?: VueClass;
}

const props = withDefaults(defineProps<KaSwitchProps>(), {
  modelValue: false,
  class: '',
  labelClass: '',
  descriptionClass: '',
});

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'change', value: boolean): void;
  (e: 'blur', event: Event): void;
}>();

const attrs = useAttrs();
const slots = useSlots();

const id = (attrs.id as string) || props.name;
const { dataTestid, suffix } = useDataTestid(id);
const labelId = `${id}-label`;
const descriptionId = props.description ? `${id}-description` : undefined;
const shouldDisplayDescription = computed(
  () => (props.description || slots.description) && !props.hideDescription,
);

const enabled = ref(props.modelValue);

watch(
  () => enabled.value,
  (val) => {
    enabled.value = val;
    emit('update:modelValue', val);
    emit('change', val);
  },
);

watch(
  () => props.modelValue,
  (val) => {
    enabled.value = val;
  },
);
const switchRoot = ref<HTMLButtonElement | null>(null);
const descRef = ref<HTMLElement | null>(null);
const labelRef = ref<HTMLElement | null>(null);
function setAriaLabel() {
  const desc = dom(descRef)?.textContent || props.description || '';
  const label = dom(labelRef)?.textContent || props.label || '';

  dom(switchRoot)?.setAttribute('aria-label', `${label}${desc ? ' ' + desc : ''}`);
}

onMounted(() => {
  setAriaLabel();
});
</script>

<template>
  <div :class="cnMerge('flex gap-4', props.class)" :data-testid="suffix('container')">
    <SwitchRoot
      :id="id"
      ref="switchRoot"
      v-model="enabled"
      :disabled="props.disabled"
      :data-testid="dataTestid"
      :class="
        cnMerge(
          switchVariants({
            value: modelValue,
            disabled: props.disabled,
            size: props.small ? 'small' : 'base',
          }),
        )
      "
      v-bind="attrs"
      @blur="emit('blur', $event)"
    >
      <SwitchThumb
        aria-hidden="true"
        :class="[
          props.small ? 'h-3 w-3 scale-[115%]' : 'h-5 w-5 scale-[105%]',
          props.modelValue && props.small ? 'translate-x-4' : 'translate-x-0',
          props.modelValue && !props.small ? 'translate-x-5' : 'translate-x-0',
          'pointer-events-none inline-block transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
        ]"
      />
    </SwitchRoot>
    <label
      :id="labelId"
      :for="id"
      :data-testid="suffix('label-container')"
      :class="
        cnMerge('m-0 -mt-1.5 select-none p-0', {
          'sr-only': props.hideLabel && !shouldDisplayDescription,
          'mt-0':
            (props.hideLabel && !shouldDisplayDescription) ||
            (!props.small &&
              (!shouldDisplayDescription || (props.hideLabel && shouldDisplayDescription))),
          '-mt-1':
            props.small &&
            (!shouldDisplayDescription || (props.hideLabel && shouldDisplayDescription)),
        })
      "
    >
      <span
        ref="labelRef"
        :data-testid="suffix('label')"
        :class="
          cnMerge(
            'm-0 flex items-center p-0 text-sm font-semibold leading-6 text-gray-900 dark:text-gray-50',
            { 'sr-only': props.hideLabel },
            props.labelClass,
          )
        "
      >
        <slot name="label">
          {{ props.label }}
        </slot>
      </span>
      <p
        v-if="props.description || $slots.description"
        :id="descriptionId"
        ref="descRef"
        :data-testid="suffix('description')"
        :class="
          cnMerge(
            'm-0 p-0 text-sm font-normal text-gray-700 dark:text-gray-400',
            { 'sr-only': props.hideDescription },
            props.descriptionClass,
          )
        "
      >
        <slot name="description" :description="description">
          {{ props.description }}
        </slot>
      </p>
    </label>
  </div>
</template>
